import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GENERATE_PICKING_LIST_PDF } from 'api/graphql/queries/order/getPickingList';
import { Spinner } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListCheck } from '@fortawesome/free-solid-svg-icons';
import { Order as OrderType } from 'data/e-commerce/orders';
import { showErrorToast } from './ToastManager';
import { useTranslation } from 'react-i18next';

interface PickingListButtonProps {
  selectedRows: OrderType[];
  onError: (message: string) => void;
}

const PickingListButton: React.FC<PickingListButtonProps> = ({ 
  selectedRows, 
  onError 
}) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);

  // Query for generating picking list PDF
  const [generatePickingList, { loading: generatingPDF }] = useLazyQuery(
    GENERATE_PICKING_LIST_PDF,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        setIsGenerating(false);
        
        if (data?.generatePickingListPdf?.success) {
          // Create a blob from the PDF content
          const byteCharacters = atob(data.generatePickingListPdf.content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: data.generatePickingListPdf.mime_type });
          
          // Create a download link and trigger it
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = data.generatePickingListPdf.filename || 'picking-list.pdf';
          document.body.appendChild(link);
          link.click();
          
          // Safely remove the link only if it's still a child of body
          if (link.parentNode === document.body) {
            document.body.removeChild(link);
          }
        } else if (data?.generatePickingListPdf?.message) {
          // If there's an error message in the response
          const errorMessage = data.generatePickingListPdf.message;
          showErrorToast(errorMessage);
          onError(errorMessage);
        } else {
          // Generic error if no specific message is provided
          const errorMessage = "Failed to generate picking list. Please try again.";
          showErrorToast(errorMessage);
          onError(errorMessage);
        }
      },
      onError: (error) => {
        setIsGenerating(false);
        const errorMessage = error.message || "An error occurred while generating the picking list.";
        showErrorToast(errorMessage);
        onError(errorMessage);
      }
    }
  );
  
  // Handler for generating picking list
  const handleGeneratePickingList = () => {
    if (selectedRows.length === 0) return;
    
    setIsGenerating(true);
    
    // Convert order IDs to integers and ensure they are passed as a single flat array
    const orderIds = selectedRows.map(row => parseInt(row.orderId.toString(), 10));
    
    generatePickingList({
      variables: {
        orderIds: orderIds
      }
    });
  };

  return (
    <Button 
      variant="warning" 
      className="me-2"
      disabled={selectedRows.length === 0 || isGenerating}
      onClick={handleGeneratePickingList}
    >
      {isGenerating ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
            style={{ width: '0.9rem', height: '0.9rem' }}
          />
          <span>{t('orderButtons.generating')}</span>
        </>
      ) : (
        <>
          <FontAwesomeIcon icon={faListCheck} className="me-2" />
          {t('orderButtons.pickupList')}
        </>
      )}
    </Button>
  );
};

export default PickingListButton;
